import { useQueryClient } from 'react-query';
import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { stats } from '@fiverr-private/obs';
import {
    AdditionalDataRequest,
    BriefClassificationResponse,
    extractAdditionalData,
    getClassification,
} from '../../api';
import { retryWrapper } from '../../utils';
import { reportTimeMetric } from '../../monitoring';
import { METRICS_PREFIX } from '../../constants';
import { checkClassificationResponseIsValid, handleClassificationError } from './utils';
import { QUERY_RETRY_DELAY_MS, QUERY_TIMEOUT_MS } from './constants';

export interface PostAndPollAdditionalDataParams {
    leafClassification: AdditionalDataRequest['leaf_classification'];
    userInput: AdditionalDataRequest['user_input'];
    contextId: AdditionalDataRequest['context_id'];
    extractors: AdditionalDataRequest['extractors'];
    metadata?: AdditionalDataRequest['metadata'];
    abortController: AbortController;
    requestType: string;
    postRetries?: number;
    pollTimeout?: number;
    flowRetries?: number;
    tierData?: AdditionalDataRequest['tier_data'];
}

/**
 * Also handles error/success logging and stats
 */
export const usePostAndPollAdditionalData = () => {
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const handleAdditionalDataResponse = ({
        additionalDataResponse,
        requestType,
        isAborted,
        requestStartTime,
        timeout,
    }: {
        additionalDataResponse: AxiosResponse<BriefClassificationResponse>;
        requestType: string;
        isAborted: boolean;
        requestStartTime: number;
        timeout?: number;
    }): BriefClassificationResponse | undefined => {
        if (
            !checkClassificationResponseIsValid({
                classificationResponse: additionalDataResponse,
                isAborted,
                requestType,
            })
        ) {
            return;
        }

        stats.count(`${METRICS_PREFIX}.${requestType}`, 'success');
        reportTimeMetric({
            metricName: requestType,
            time: Date.now() - requestStartTime,
            timeout,
        });
        return additionalDataResponse.data;
    };

    const postAndPollAdditionalData = async ({
        leafClassification,
        userInput,
        contextId,
        requestType,
        extractors,
        tierData,
        abortController,
        metadata,
        postRetries = 2,
        pollTimeout = QUERY_TIMEOUT_MS,
        flowRetries = 2,
    }: PostAndPollAdditionalDataParams): Promise<BriefClassificationResponse | undefined> => {
        setIsLoading(true);
        const requestStartTime = Date.now();
        return retryWrapper<BriefClassificationResponse | undefined>({
            retries: flowRetries,
            metric: requestType,
            wrappedFn: async () => {
                try {
                    const {
                        data: { poll_id: classificationPollingId },
                    } = await queryClient.fetchQuery({
                        queryKey: [requestType, JSON.stringify(leafClassification)],
                        queryFn: () =>
                            extractAdditionalData({
                                leaf_classification: leafClassification,
                                user_input: userInput,
                                context_id: contextId,
                                extractors,
                                metadata,
                                tier_data: tierData,
                            }),
                        cacheTime: 0,
                        retry: postRetries,
                    });

                    if (!classificationPollingId) {
                        handleClassificationError({ requestType });
                        throw new Error('Failed to POST extract additional data');
                    }

                    let result;
                    try {
                        result = await queryClient.fetchQuery({
                            queryKey: [requestType, classificationPollingId],
                            queryFn: () =>
                                getClassification({
                                    pollingId: classificationPollingId,
                                    inspectedField: 'resolved_data',
                                    abortController,
                                }),
                            retry: pollTimeout / QUERY_RETRY_DELAY_MS,
                            retryDelay: QUERY_RETRY_DELAY_MS,
                        });
                    } catch (pollingError: any) {
                        // If polling times out, try to get the current state anyway
                        if (pollingError?.message === 'No data') {
                            try {
                                // Make a direct call to get whatever data is available
                                const partialResult = await getClassification({
                                    pollingId: classificationPollingId,
                                    inspectedField: 'resolved_data',
                                    abortController,
                                    skipDataValidation: true,
                                });

                                // Return partial result even if resolved_data is null
                                return handleAdditionalDataResponse({
                                    additionalDataResponse: partialResult,
                                    requestType,
                                    isAborted: abortController.signal.aborted,
                                    requestStartTime,
                                    timeout: pollTimeout,
                                });
                            } catch (finalError) {
                                // If we can't even get partial data, throw the original error
                                throw pollingError;
                            }
                        } else {
                            // Re-throw non-timeout errors
                            throw pollingError;
                        }
                    }

                    return handleAdditionalDataResponse({
                        additionalDataResponse: result,
                        requestType,
                        isAborted: abortController.signal.aborted,
                        requestStartTime,
                        timeout: pollTimeout,
                    });
                } catch (error: any) {
                    const isAborted = abortController.signal.aborted;

                    handleClassificationError({
                        requestType,
                        errorMessage: isAborted
                            ? 'Request was cancelled'
                            : error?.message?.toString() || error?.toString() || 'Unknown error',
                        isTimeout: error?.message === 'No data',
                        isAborted,
                    });
                    throw error;
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    return {
        postAndPollAdditionalData,
        isLoading,
    };
};
